import { getSharedSchedulePartialWrite } from './errors';
import type { SharedScheduleFailure } from './errors';

/**
 * 등록 화면이 더 제출해도 되는 상태인가.
 *  - `OPEN`: 평소. 제출할 수 있다.
 *  - `MUST_REVIEW_SCHEDULE_LIST`: 무엇이 등록됐는지 목록에서 확인해야 한다. 이 화면에서는
 *    더 제출할 수 없고, 사용자를 일정 목록으로 보낸다.
 *
 * ⚠️ **이 잠금은 중복을 막는 장치가 아니다. 직원 안내용이다.**
 *
 * 중복의 실제 방어는 **서버**가 한다 — 같은 회원·같은 시각·같은 강사의 활성 일정이 이미 있으면
 * 409 `SHARED_MEMBERSHIP_DUPLICATE_SCHEDULE` 로 요청 전체를 거절한다(아무것도 만들지 않는다).
 *
 * 이 상태는 컴포넌트 로컬 ref 에 살아서 화면을 떠나거나 새로고침하면 사라지고, 다른 탭에는
 * 애초에 없다. 그래서 여기서 완벽을 노리지 않는다. 하는 일은 "지금 뭔가 잘못됐으니 목록부터
 * 보라"고 **빨리 알리는 것**이고, 그 사이로 새는 재제출은 서버가 받는다.
 */
export type ResubmitGuardState = 'OPEN' | 'MUST_REVIEW_SCHEDULE_LIST';

/**
 * 이 실패 응답이 "직원이 목록을 먼저 봐야 하는" 상태를 만드는가.
 *
 * **근거는 `partialWriteMayExist` 하나다(계약 §2-4).** 일정이 남았을 수 있으면
 * **키를 버렸든 아니든** 목록을 봐야 한다.
 *
 * ⚠️ `retryPolicy` 를 끌어들이면 안 된다. 한때 "키를 버렸다 + 남았을 수 있다"의 **결합**으로
 * 판단했는데 구멍이 났다. `{A,B}` 중 A 성공, B 는 **쓰기 전** 실패면 서버가
 * `SAME_KEY` + `partialWriteMayExist: true` 를 준다(백엔드 테스트가 보장한다).
 * 그때 "같은 키가 나가니 멱등 재사용된다"고 봤지만, 그건 **같은 입력일 때만** 참이다.
 * 입력을 바꾸면 intentKey 가 달라져 새 키가 나가고 서버는 남아 있는 A 를 못 알아본다.
 * 두 질문을 섞지 않는다(§2-2-1) — `retryPolicy` 는 **키 폐기 판정에만** 쓴다.
 *
 * 잠그지 않는 경우(서버가 "남지 않았다"고 말하거나 알 수 없을 때):
 *  - `partialWriteMayExist: false` → 서버의 명시적 부정
 *  - 필드 자체가 없음(구서버) → 근거 없이 막지 않는다
 *
 * 반대로 `partialWriteMayExist: true` 인데 실제로는 아무것도 안 만들어졌을 수 있다
 * (서버가 보수적으로 붙인다). 그때 목록으로 보내는 손해는 작다 — 직원이 보고 다시 등록하면
 * 된다. 놓치면 중복 일정과 정산 오류다.
 */
export const armsResubmitGuard = (result: SharedScheduleFailure | null | undefined): boolean =>
    getSharedSchedulePartialWrite(result)?.mayExist === true;

/**
 * 실패 응답을 받은 뒤의 상태.
 *
 * **입력 내용은 보지 않는다.** 예전에는 같은 intentKey 일 때만 막았는데, 부분 성공에서
 * **어느 항목이 살아남았는지 모르므로** "입력이 다르니 안전하다"고 판단할 근거가 없었다.
 * 실제로 두 명 중 한 명만 바꾸거나 회원 선택 순서만 바꿔도 intentKey 가 달라져 차단이 샜고,
 * 생존한 항목이 다시 만들어졌다. materializedSchedules 로 생존분을 가릴 수도 없다 —
 * 누락·과다가 가능한 힌트다(계약 §1-3).
 *
 * 한 번 걸리면 풀리지 않는다. 푸는 길은 목록을 확인하러 화면을 떠나는 것뿐이고,
 * 그때 컴포넌트가 언마운트되며 상태가 자연히 사라진다. 그 뒤 다시 들어와 같은 내용을
 * 등록하면 **서버가** 중복을 거절한다(SHARED_MEMBERSHIP_DUPLICATE_SCHEDULE).
 */
export const nextResubmitGuardState = (
    current: ResubmitGuardState,
    result: SharedScheduleFailure | null | undefined,
): ResubmitGuardState =>
    current === 'MUST_REVIEW_SCHEDULE_LIST' || armsResubmitGuard(result)
        ? 'MUST_REVIEW_SCHEDULE_LIST'
        : current;

/** 이 화면에서 더 제출할 수 없는가. */
export const isResubmitBlocked = (state: ResubmitGuardState): boolean =>
    state === 'MUST_REVIEW_SCHEDULE_LIST';

/** 왜 목록으로 보내는지 알리는 문구. 원인 문구 뒤에 붙여 한 알럿으로 띄운다. */
export const MUST_REVIEW_SCHEDULE_LIST_NOTICE =
    '일부 수업이 이미 등록됐을 수 있습니다\n일정 목록에서 확인한 뒤 다시 등록해주세요';
